/** Gemeinsame Kleinteile des Kapitels „Kanäle & Werbeflächen" (Plan 2026-10-20, Umbau 2026-10-23). */
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import InfoTip from '../../../components/InfoTip';
import { Spalte } from '../../../components/auswertungen/SortableTable';
import type { Breite } from '../../../components/auswertungen/spaltenwahl';
import {
  BezahltBuendel,
  fest,
  int,
  nachGruppenReihenfolge,
  zahlerHinweis,
  Quote,
  spanne,
  tEuro,
  VeraenderungWert,
} from '../../../components/auswertungen/kennzahlTeile';
import {
  GrauSchwellen,
  KANAL_FENSTER,
  KanalFensterMonate,
  KanalKennzahlen,
  VergleichsBasis,
  VergleichsFenster,
  Zeitfenster,
  formatBerlinWallclock,
} from '../../../helpers/auswertungenData';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';

// Seit Stufe 0 des P1/P2-Umbaus (Plan 2026-10-23-p1-p2-kpi-review-umbau, E7.2) liegen die geteilten Teile in
// `components/auswertungen/kennzahlTeile.tsx`; hier nur re-exportiert, damit die P3-Importe unverändert gelten.
export {
  datenstandSatz,
  ENG_UNTER,
  Etikett,
  euroJeTausend,
  fest,
  formatDelta,
  jeTausend,
  Kachel,
  kompakt,
  spanne,
  tEuro,
  Teile,
  useBreitePx,
  VeraenderungWert,
  veraenderungTip,
  zahlerHinweis,
} from '../../../components/auswertungen/kennzahlTeile';

/**
 * Lädt bei jedem neuen Fenster neu. Beim Wechsel wird die alte Antwort SOFORT verworfen (sonst stünden die
 * Zahlen des alten Fensters unter der neuen Fensterangabe, bis die Antwort kommt) — Abbruch-Flag gegen späte
 * Antworten (`.claude/rules/react-frontend.md` § Async fetch race conditions).
 */
export function useLadenFenster<T>(fetcher: (m: KanalFensterMonate) => Promise<T>, monate: KanalFensterMonate) {
  const [data, setData] = useState<T | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFehler(null);
    fetcher(monate)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) setFehler(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, monate]);
  return { data, fehler };
}

/** Fensterwahl für das ganze Kapitel (F6, F8, Quellen-Effizienz, Blog). */
export const FensterWahl: FC<{ monate: KanalFensterMonate; onChange: (m: KanalFensterMonate) => void }> = ({ monate, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      Zeitraum
    </Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={monate}
      onChange={(_e, v: KanalFensterMonate | null) => v && onChange(v)}
      aria-label="Zeitraum der Auswertung"
    >
      {KANAL_FENSTER.map((m) => (
        <ToggleButton key={m} value={m} sx={{ px: 1.5, textTransform: 'none' }}>
          {m} Monate
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
    {/* Zusatz nach vorschlag_v3 §4.1 — sonst wirkt das frühere Ende der Flächen-Karten wie ein Fehler. */}
    <Typography variant="body2" sx={{ color: 'text.secondary', flex: '1 1 320px', maxWidth: '75ch' }}>
      Gilt für alle Karten außer „Versände der letzten Woche“ (eigene Wochenwahl). Die Werbeflächen-Karten (Rang, Einladungen, Bestell-Links) enden 14 Tage früher, weil ihre Anfos noch nachkommen; der Verlauf
      zeigt immer die letzten 12 oder 24 Kalendermonate.
    </Typography>
  </Box>
);

const BASIS_SPEICHER = 'auswertungen.vergleichsbasis';

/**
 * Vergleichsbasis der Veränderung (Till 23.10.2026, E6): Vorjahreszeitraum oder gleich langer Zeitraum direkt davor.
 * Standard Vorjahr; die Wahl gilt je Browser (localStorage nur im try/catch — privates Fenster wirft).
 */
export function useVergleichsbasis(): [VergleichsBasis, (b: VergleichsBasis) => void] {
  const [basis, setBasis] = useState<VergleichsBasis>(() => {
    try {
      return window.localStorage.getItem(BASIS_SPEICHER) === 'vorher' ? 'vorher' : 'vorjahr';
    } catch {
      return 'vorjahr';
    }
  });
  const setze = useCallback((b: VergleichsBasis) => {
    setBasis(b);
    try {
      window.localStorage.setItem(BASIS_SPEICHER, b);
    } catch {
      /* nur Komfort */
    }
  }, []);
  return [basis, setze];
}

/**
 * Satz, der nur für die Kanal-Sicht gilt (P2-V3 § 4.3: aus `TERMS.veraenderungVorjahr.tip` hierher verschoben, damit
 * P1/P2 den Begriff ohne Kanal-Satz lesen). `veraenderungVorzeitraum` liest nur P3 und trägt ihn weiter selbst.
 */
const KANAL_KENNUNG_SATZ = 'Verschiebungen zwischen Kanälen können auch aus geänderten Kennungen in den Bestell-Links kommen.';

/** Begriff der gewählten Basis (Label, Kurzform, Erklärung) — aus TERMS; beim Vorjahr mit dem Kanal-Satz der P3-Sicht. */
export const basisBegriff = (b: VergleichsBasis) =>
  b === 'vorjahr'
    ? { ...TERMS.veraenderungVorjahr, tip: `${TERMS.veraenderungVorjahr.tip} ${KANAL_KENNUNG_SATZ}` }
    : TERMS.veraenderungVorzeitraum;

/**
 * Umschalter „ggü. Vorjahr | ggü. Vorzeitraum" im Seitenkopf; gilt für Kachel, Kanal- und Blog-Tabelle. Bei 12 Monaten
 * gilt das Vorjahr (Till 23.10.2026, E6: „12 Monate = Vorjahr") — gesperrt, mit Satz statt stiller Wirkungslosigkeit.
 * Der Aufrufer sperrt nach der Fensterlänge, nicht nach gleichen Zeitstempeln: Seit dem Review schließt der Vorzeitraum
 * sekundengenau an und liegt einen Tag neben dem Vorjahr, die Sperre griff dadurch nie (B49/B61/B73, 26.10.2026).
 */
export const BasisWahl: FC<{ basis: VergleichsBasis; onChange: (b: VergleichsBasis) => void; gleich: boolean }> = ({
  basis,
  onChange,
  gleich,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
      Veränderung
      <InfoTip
        text={`${TERMS.veraenderungVorjahr.label}: ${basisBegriff('vorjahr').tip} — ${TERMS.veraenderungVorzeitraum.label}: ${TERMS.veraenderungVorzeitraum.tip}`}
      />
    </Typography>
    <ToggleButtonGroup
      size="small"
      exclusive
      value={basis}
      disabled={gleich}
      onChange={(_e, v: VergleichsBasis | null) => v && onChange(v)}
      aria-label="Vergleichsbasis der Veränderung"
    >
      <ToggleButton value="vorjahr" sx={{ px: 1.5, textTransform: 'none' }}>
        {TERMS.veraenderungVorjahr.short}
      </ToggleButton>
      <ToggleButton value="vorher" sx={{ px: 1.5, textTransform: 'none' }}>
        {TERMS.veraenderungVorzeitraum.short}
      </ToggleButton>
    </ToggleButtonGroup>
    <Typography variant="body2" sx={{ color: 'text.secondary', flex: '1 1 320px', maxWidth: '75ch' }}>
      {gleich
        ? 'Bei 12 Monaten gilt immer das Vorjahr: Der gleich lange Zeitraum direkt davor ist fast derselbe (er liegt nur einen Tag daneben).'
        : 'Gilt für die drei Kacheln darunter und die Spalte „Veränderung“ der Kanal-Tabelle.'}
    </Typography>
  </Box>
);

/** Vergleichsfenster der gewählten Basis als Text. */
export const basisFenster = (v: VergleichsFenster, b: VergleichsBasis): string => {
  const f = b === 'vorjahr' ? v.vorjahr : v.vorher;
  return spanne(f.erster_tag, f.letzter_tag);
};

/**
 * Tatsächlich gezählter Zeitraum der Karte: vom ersten Tag bis zum Datenstand von PL-X („27.04.–25.10.2026, 22:59"),
 * nicht bis zum exklusiven Fensterende — so sehen beide Zeiträume gleich lang aus, wie sie es sind (B50/B74).
 */
export const jetztSpanne = (v: VergleichsFenster): string => {
  const s = spanne(v.jetzt.erster_tag, v.jetzt.letzter_tag);
  return v.datenstand && v.datenstand.slice(0, 10) === v.jetzt.letzter_tag ? `${s}, ${v.datenstand.slice(11, 16)}` : s;
};

/**
 * Satz zu den Zeitraumgrenzen für den Tooltip der Veränderung (ersetzt in P3 `datenstandSatz`, der für den Vorzeitraum
 * falsch war: Der endet direkt vor dem Zeitraum, nicht am Datenstand — B63).
 */
export const fensterSatz = (v: VergleichsFenster, b: VergleichsBasis): string => {
  const ende = v.datenstand ? `endet am Datenstand von PL-X (${formatBerlinWallclock(v.datenstand)})` : 'endet am Stichtag';
  const basis =
    b === 'vorjahr'
      ? `Der Vergleich ist derselbe Zeitraum ein Jahr früher (${spanne(v.vorjahr.erster_tag, v.vorjahr.letzter_tag)}), bis zur selben Uhrzeit.`
      : `Der Vergleich ist gleich lang und endet direkt vor dem Zeitraum (${spanne(v.vorher.erster_tag, v.vorher.letzter_tag)}).`;
  return `Der Zeitraum ${spanne(v.jetzt.erster_tag, v.jetzt.letzter_tag)} ${ende}. ${basis}`;
};

/** Reife Spannen der Bezahlquote (`KanaeleAntwort.reif_vergleich`, Plan 2026-10-30-vergleichsfenster-drift). */
export type ReifSpannen = Partial<Record<'jetzt' | VergleichsBasis, Zeitfenster | null>>;

/** Reife Spanne der Basis als Text, für das Etikett der Bezahlquote; ohne Antwortfeld das ganze Vergleichsfenster. */
export const reifBasisFenster = (r: ReifSpannen | undefined, v: VergleichsFenster, b: VergleichsBasis): string => {
  const f = r?.[b];
  return f ? spanne(f.erster_tag, f.letzter_tag) : basisFenster(v, b);
};

/**
 * Satz zur Bezahlquote: Sie rechnet nur über Anfos, die heute mindestens 60 Tage alt sind, und die Basis über GENAU diese
 * Spanne verschoben — sonst stünde ein kürzerer Zeitraum gegen einen längeren (bei drei Monaten ein reifer gegen drei).
 */
export const reifSatz = (r: ReifSpannen | undefined, b: VergleichsBasis, reifeTage: number): string => {
  const j = r?.jetzt;
  const f = r?.[b];
  if (!j || !f) return `Noch keine Anfo in diesem Zeitraum ist ${reifeTage} Tage alt.`;
  const wie = b === 'vorjahr' ? 'ein Jahr früher' : 'direkt davor';
  return `Bezahlquote nur über Anfos, die heute mindestens ${reifeTage} Tage alt sind: ${spanne(j.erster_tag, j.letzter_tag)}, verglichen mit derselben Spanne ${wie} (${spanne(f.erster_tag, f.letzter_tag)}).`;
};

/** Exklusives Fensterende (YYYY-MM-DD) → letzter gezählter Tag. */
export const vorTag = (bisExklusiv: string): string => {
  const d = new Date(`${bisExklusiv.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

/** „vom 21.04. bis 20.10.2026" — `bis` ist exklusiv (Folgetag). */
export function fensterText(von: string, bisExklusiv: string): string {
  const d = new Date(`${bisExklusiv}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  const bis = d.toISOString().slice(0, 10);
  return `${formatBerlinWallclock(von)} bis ${formatBerlinWallclock(bis)}`;
}

/** „23.05." bzw. mit Jahr „23.05.26", wenn der Zeitraum über einen Jahreswechsel geht. */
const kurzDatum = (iso: string, mitJahr: boolean): string =>
  `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${mitJahr ? iso.slice(2, 4) : ''}`;

/**
 * Versandzeitraum einer Fläche, wenn er weniger als drei Viertel des Fensters abdeckt (spät gestartet, eingestellt)
 * — sonst `null`. Grund: sonst vergleicht man Jahreszeiten statt Flächen (Persona-Runde, Till 20.10.2026). Eine
 * schärfere Grenze (7 Tage) markierte am 20.10. fast jede Zeile, weil Heat-Ups erst mit dem ersten Webinar im
 * Fenster beginnen — die Angabe wurde zum Rauschen und verdeckte die Flächen, die wirklich kürzer laufen.
 */
export function teilzeitraum(
  erster: string | null,
  letzter: string | null,
  fensterVon: string,
  versandBisExklusiv: string,
): string | null {
  if (!erster || !letzter) return null;
  const tage = (a: string, b: string) => (Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000;
  const abdeckung = (tage(erster, letzter) + 1) / Math.max(1, tage(fensterVon, versandBisExklusiv));
  if (abdeckung >= 0.75) return null;
  const jahr = erster.slice(0, 4) !== letzter.slice(0, 4);
  if (erster === letzter) return `nur am ${kurzDatum(erster, true)}`;
  return `nur ${kurzDatum(erster, jahr)}–${kurzDatum(letzter, jahr)}`;
}

/** Farbfeld einer Kanalgruppe (Rechteck wie die Balken). */
export const Farbfeld: FC<{ color: string }> = ({ color }) => (
  <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '3px', backgroundColor: color, flexShrink: 0, mr: 1, verticalAlign: 'middle' }} />
);

/** Unter so vielen reifen Anfos steht eine Bezahlquote grau (`Quote` in webinare-produkte/shared). */
export const WENIG_REIF = 20;

/** Anteil als Zahl mit dünnem Balken darunter (linear, 0–100 %). */
const Anteil: FC<{ share: number | null; color: string }> = ({ share, color }) => (
  <Box sx={{ minWidth: 56 }}>
    {formatPct(share)}
    <Box sx={{ height: 4, borderRadius: 2, backgroundColor: 'action.hover', mt: 0.5 }}>
      <Box sx={{ height: 4, borderRadius: 2, width: `${Math.min(100, share ?? 0)}%`, backgroundColor: color }} />
    </Box>
  </Box>
);

/** Menügruppen der Kanal-/Blog-Spalten in fester Reihenfolge (= Bedeutung, vorschlag_v3 §5.3). */
export const KANAL_GRUPPEN = ['Menge', 'Wert', 'Trend', 'Kundentyp', 'Zahlung (wächst mit dem Alter)'] as const;

/** Pflicht: Name · Anfos · Erwarteter Wert. Standard ab 640 px Kartenbreite nach E9 (7 Spalten), darunter nur Pflicht. */
const KANAL_BREIT = ['name', 'anfos', 'anteil', 'wert', 'erstbesteller', 'quote', 'veraenderung'] as const;
export const KANAL_STANDARDS: Record<Breite, readonly string[]> = {
  breit: KANAL_BREIT,
  mittel: KANAL_BREIT,
  schmal: ['name', 'anfos', 'wert'],
};

export interface KennzahlSpaltenKontext<T extends KanalKennzahlen> {
  basis: VergleichsBasis;
  schwellen: GrauSchwellen;
  reifeTage: number;
  /** Summenzeile (Backend); null, solange nichts geladen ist. */
  total: KanalKennzahlen | null;
  /** Farbe des Anteil-Balkens einer Zeile. */
  farbe: (r: T) => string;
  /** Aktuell sichtbare Spalten — für die Kopplungen (Anteil unter Anfos, ▲/▼ am Namen). */
  sichtbar: ReadonlySet<string>;
}

/**
 * Die Kennzahl-Spalten der Kanal- und der Blog-Karte, in Anzeigereihenfolge (E9: Veränderung zuletzt). Beide Karten
 * teilen Liste, Standards und gespeicherte Wahl (vorschlag_v3 §2.6); die Namensspalte baut jede Karte selbst.
 * Grau-Schwellen kommen aus der API (`grau_schwellen`), keine Zahl ist hier fest.
 */
export function kennzahlSpalten<T extends KanalKennzahlen>(c: KennzahlSpaltenKontext<T>): Spalte<T>[] {
  const { schwellen: sw, total: t, reifeTage } = c;
  /** Summenzeile nur mit geladener Antwort (die Spalten werden auch vorher schon fürs Menü gebaut). */
  const summe = (f: (k: KanalKennzahlen) => ReactNode): ReactNode | undefined => (t ? f(t) : undefined);
  const begriff = basisBegriff(c.basis);
  const veraenderung = (r: KanalKennzahlen) => (c.basis === 'vorjahr' ? r.veraenderung.vorjahr : r.veraenderung.vorher);
  const wenig = TERMS.wenigDaten.etikett.anfos;
  const reifGrau = (r: KanalKennzahlen) => (r.mature_anfos < sw.bezahlquote_reife_anfos ? `${wenig} ab ${reifeTage} Tagen` : null);
  const anteilGrau = (r: KanalKennzahlen) => (r.typed < sw.anteil_anfos ? wenig : null);
  const kundentyp = (r: KanalKennzahlen, n: number) => (r.typed > 0 ? (n / r.typed) * 100 : null);
  const anfosZelle = (r: KanalKennzahlen) =>
    c.sichtbar.has('anteil') ? (
      int(r.anfos)
    ) : (
      // Ohne Anteil-Spalte (Handy-Standard „Anfos (Anteil)") steht der Anteil darunter.
      <Box sx={{ lineHeight: 1.25 }}>
        {int(r.anfos)}
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
          {fest(formatPct(r.share))}
        </Box>
      </Box>
    );
  const wertZelle = (r: KanalKennzahlen) => (
    <Box sx={{ lineHeight: 1.25 }}>
      {tEuro(r.value_sum)}
      <Box
        component="span"
        title={r.with_value < sw.anteil_anfos ? `Ø grau: unter ${int(sw.anteil_anfos)} Anfos mit Wert` : undefined}
        sx={{ display: 'block', fontSize: 11, color: r.with_value < sw.anteil_anfos ? 'text.disabled' : 'text.secondary' }}
      >
        Ø {fest(formatEur(r.value_per_anfo, 0))}
      </Box>
    </Box>
  );
  return [
    {
      key: 'anfos',
      label: TERMS.anfos.label,
      tip: TERMS.anfos.tip,
      einheit: 'Anzahl',
      align: 'right',
      gruppe: 'Menge',
      pflicht: true,
      render: (r) => anfosZelle(r),
      sortValue: (r) => r.anfos,
      summary: summe((t) => anfosZelle(t)),
    },
    {
      key: 'anteil',
      label: TERMS.anteilAnAllenAnfos.label,
      tip: TERMS.anteilAnAllenAnfos.tip,
      einheit: 'in %',
      align: 'right',
      gruppe: 'Menge',
      render: (r) => <Anteil share={r.share} color={c.farbe(r)} />,
      sortValue: (r) => r.share,
      summary: summe((t) => formatPct(t.share ?? 100)),
    },
    {
      key: 'wert',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertBeiBestellung.tip,
      hinweis: `Summe der Zeile, darunter Ø je Anfo mit Wert; der Ø steht grau unter ${int(sw.anteil_anfos)} Anfos mit Wert.`,
      einheit: 'Summe · Ø je Anfo',
      align: 'right',
      gruppe: 'Wert',
      pflicht: true,
      render: (r) => wertZelle(r),
      sortValue: (r) => r.value_sum,
      summary: summe((t) => wertZelle(t)),
    },
    {
      key: 'erstbesteller',
      label: TERMS.anteilErstbesteller.label,
      tip: TERMS.anteilErstbesteller.tip,
      hinweis: `Grau unter ${int(sw.anteil_anfos)} Anfos.`,
      einheit: 'in %',
      align: 'right',
      gruppe: 'Kundentyp',
      render: (r) => formatPct(r.first_buyer_share),
      sortValue: (r) => r.first_buyer_share,
      unsicher: anteilGrau,
      summary: summe((t) => formatPct(t.first_buyer_share)),
    },
    {
      key: 'frueherGezahlt',
      label: TERMS.frueherGezahlt.label,
      tip: TERMS.frueherGezahlt.tip,
      hinweis: `Anteil an den Anfos der Zeile; grau unter ${int(sw.anteil_anfos)} Anfos.`,
      einheit: 'in %',
      align: 'right',
      gruppe: 'Kundentyp',
      render: (r) => formatPct(kundentyp(r, r.customer.frueher_gezahlt)),
      sortValue: (r) => kundentyp(r, r.customer.frueher_gezahlt),
      unsicher: anteilGrau,
      summary: summe((t) => formatPct(kundentyp(t, t.customer.frueher_gezahlt))),
    },
    {
      key: 'nieGezahlt',
      label: TERMS.frueherNieGezahlt.label,
      tip: TERMS.frueherNieGezahlt.tip,
      hinweis: `Anteil an den Anfos der Zeile; grau unter ${int(sw.anteil_anfos)} Anfos.`,
      einheit: 'in %',
      align: 'right',
      gruppe: 'Kundentyp',
      render: (r) => formatPct(r.never_paid_share),
      sortValue: (r) => r.never_paid_share,
      unsicher: anteilGrau,
      summary: summe((t) => formatPct(t.never_paid_share)),
    },
    {
      key: 'quote',
      label: TERMS.bezahlquote.label,
      tip: TERMS.bezahlquote.tip,
      hinweis: `Grau unter ${int(sw.bezahlquote_reife_anfos)} Anfos ab ${reifeTage} Tagen. ${TERMS.abweichungMarke.tip}`,
      einheit: 'in %',
      align: 'right',
      gruppe: 'Zahlung (wächst mit dem Alter)',
      render: (r) => <Quote rate={r.pay_rate} bezahlt={r.mature_paid} reif={r.mature_anfos} marke={r.pay_rate_flag} />,
      sortValue: (r) => r.pay_rate,
      unsicher: reifGrau,
      summary: summe((t) => <Quote rate={t.pay_rate} bezahlt={t.mature_paid} reif={t.mature_anfos} />),
    },
    {
      key: 'bezahltJeAnfo',
      label: TERMS.bezahltJeAnfo.label,
      tip: TERMS.bezahltJeAnfo.tip,
      hinweis: `Erscheint immer mit bezahlten Anfos und Ø Alter — bezahlt wächst mit dem Alter der Anfos. Grau unter ${int(sw.bezahlquote_reife_anfos)} Anfos ab ${reifeTage} Tagen. ${zahlerHinweis(reifeTage)}`,
      einheit: 'je Anfo · bezahlte Anfos · Ø Alter',
      align: 'right',
      gruppe: 'Zahlung (wächst mit dem Alter)',
      render: (r) => <BezahltBuendel wert={fest(formatEur(r.paid_per_anfo, 0))} zahler={r.payers} alterTage={r.avg_age_days} />,
      sortValue: (r) => r.paid_per_anfo,
      unsicher: reifGrau,
      summary: summe((t) => <BezahltBuendel wert={fest(formatEur(t.paid_per_anfo, 0))} zahler={t.payers} alterTage={t.avg_age_days} />),
    },
    {
      key: 'bezahltSumme',
      label: TERMS.umsatzNetto.label,
      tip: TERMS.umsatzNetto.tip,
      hinweis: `Erscheint immer mit bezahlten Anfos und Ø Alter — bezahlt wächst mit dem Alter der Anfos. ${zahlerHinweis(reifeTage)}`,
      einheit: 'Summe · bezahlte Anfos · Ø Alter',
      align: 'right',
      gruppe: 'Zahlung (wächst mit dem Alter)',
      render: (r) => <BezahltBuendel wert={tEuro(r.paid_revenue)} zahler={r.payers} alterTage={r.avg_age_days} />,
      sortValue: (r) => r.paid_revenue,
      summary: summe((t) => <BezahltBuendel wert={tEuro(t.paid_revenue)} zahler={t.payers} alterTage={t.avg_age_days} />),
    },
    {
      key: 'veraenderung',
      label: begriff.label,
      tip: begriff.tip,
      hinweis: `Erwarteter Wert gegen den Vergleichszeitraum (Umschalter oben). „neu“ = dort keine Anfo; grau unter ${int(sw.veraenderung_anfos)} Anfos in einem der beiden Zeiträume. Die Zerlegung steht im Tooltip der Zahl.`,
      einheit: 'in %',
      align: 'right',
      gruppe: 'Trend',
      render: (r) => <VeraenderungWert v={veraenderung(r)} schwelle={sw.veraenderung_anfos} />,
      sortValue: (r) => (veraenderung(r).neu ? null : veraenderung(r).delta),
      unsicher: (r) => (veraenderung(r).grau_grund ? wenig : null),
      summary: summe((t) => <VeraenderungWert v={veraenderung(t)} schwelle={sw.veraenderung_anfos} />),
    },
  ];
}

/** Spalten-Beschreibung fürs Menü in der Gruppenreihenfolge von `KANAL_GRUPPEN` (die Tabelle behält ihre Reihenfolge). */
export const nachGruppen = <M extends { gruppe: string }>(meta: M[]): M[] => nachGruppenReihenfolge(meta, KANAL_GRUPPEN);
